import { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { humanizeDocType } from '../CGReview/fieldDisplayConfig';

interface CGStageModalProps {
  registrationId: number;
  stages: any[]; // Stages Pipedrive legacy — encore passés pour compat mais non listés directement
  initialStageId: string | number;
  /** cg_status canonique actuel (depuis la colonne CG_Registrations.cg_status). Optionnel pour compat. */
  initialCgStatus?: string | null;
  /** cg_review_status v2 actuel. Sert à pré-cocher la bonne option. */
  initialCgReviewStatus?: string | null;
  onRefresh: (changeCurrentTab?: number, skipPipedriveSync?: boolean) => void;
}

// ─────────────────────────────────────────────────────────────
//  Statuts canoniques cg_status — single source of truth UI.
//  Le backend `updateCGCanonicalStatusController` mappe en interne vers
//  le tuple (StageID, cg_review_status, Visible) dans une transaction.
//  Cohérent avec `packages/backend/src/utils/cgStatus.ts`.
// ─────────────────────────────────────────────────────────────

interface CgStatusOption {
  value: string;
  label: string;
  description: string;
  /** Tag de catégorie pour grouper visuellement. */
  category: 'in_progress' | 'agency_action' | 'terminal';
  /** Couleur d'accent. */
  color: 'info' | 'warning' | 'success' | 'error' | 'default';
}

const CG_STATUS_OPTIONS: CgStatusOption[] = [
  // En cours côté Skipcar
  {
    value: 'awaiting_review',
    label: 'En review Skipcar',
    description: "Skipcar relit le dossier avant envoi à l'ANTS.",
    category: 'in_progress',
    color: 'info',
  },
  {
    value: 'awaiting_misiv',
    label: 'Démarches ANTS en cours',
    description: "Envoyé à l'ANTS via MISIV, attente du retour.",
    category: 'in_progress',
    color: 'info',
  },
  // Action attendue côté agence
  {
    value: 'awaiting_payment',
    label: 'En attente de paiement',
    description: 'Docs OK, facture émise et non réglée.',
    category: 'agency_action',
    color: 'warning',
  },
  {
    value: 'awaiting_docs',
    label: 'Pièces à déposer',
    description: "L'agence doit redéposer une ou plusieurs pièces.",
    category: 'agency_action',
    color: 'warning',
  },
  {
    value: 'awaiting_docs_and_payment',
    label: 'Paiement + pièces à déposer',
    description: "L'agence doit régler la facture ET redéposer des pièces.",
    category: 'agency_action',
    color: 'warning',
  },
  // Terminal
  {
    value: 'completed',
    label: 'Procédure effectuée',
    description: 'Carte grise livrée — dossier clos.',
    category: 'terminal',
    color: 'success',
  },
  {
    value: 'rejected',
    label: 'Rejetée',
    description: 'Dossier rejeté définitivement par Skipcar.',
    category: 'terminal',
    color: 'error',
  },
  {
    value: 'cancelled',
    label: 'Annulée',
    description: "Dossier soft-deleted (n'apparaît plus dans la liste agence).",
    category: 'terminal',
    color: 'default',
  },
];

const CG_STATUS_LABEL_BY_VALUE: Record<string, string> = CG_STATUS_OPTIONS.reduce<Record<string, string>>(
  (acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
  },
  { awaiting_reply: 'Réponse attendue', draft: 'Brouillon' },
);

const CATEGORY_LABELS: Record<CgStatusOption['category'], string> = {
  in_progress: 'En cours côté Skipcar',
  agency_action: 'Action attendue côté agence',
  terminal: 'Terminal',
};

// Dérive le cg_status canonique courant depuis l'état legacy si non fourni.
// Logique alignée sur `deriveCgStatus` côté backend.
const deriveCurrentCgStatus = (
  initialCgStatus: string | null | undefined,
  initialCgReviewStatus: string | null | undefined,
  initialStageId: string | number,
): string | null => {
  if (initialCgStatus) return initialCgStatus;
  if (initialCgReviewStatus) {
    if (initialCgReviewStatus === 'god_rejected') return 'rejected';
    if (initialCgReviewStatus === 'awaiting_payment_and_docs') return 'awaiting_docs_and_payment';
    if (initialCgReviewStatus === 'awaiting_docs') return 'awaiting_docs';
    if (initialCgReviewStatus === 'awaiting_payment') return 'awaiting_payment';
    if (initialCgReviewStatus === 'pending_god_review') return 'awaiting_review';
    if (initialCgReviewStatus === 'god_approved') return 'awaiting_misiv';
  }
  const stage = Number(initialStageId);
  if (stage === 48) return 'completed';
  if (stage === 46) return 'awaiting_payment';
  if (stage === 47) return 'awaiting_review';
  return null;
};

const CGStageModal: React.FC<CGStageModalProps> = ({
  registrationId,
  initialStageId,
  initialCgStatus = null,
  initialCgReviewStatus = null,
  onRefresh,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(() =>
    deriveCurrentCgStatus(initialCgStatus, initialCgReviewStatus, initialStageId),
  );
  const [openDocTypes, setOpenDocTypes] = useState<string[]>([]);

  useEffect(() => {
    setSelectedStatus(deriveCurrentCgStatus(initialCgStatus, initialCgReviewStatus, initialStageId));
  }, [initialCgStatus, initialCgReviewStatus, initialStageId]);

  // Les demandes de pièces encore ouvertes décident du statut réellement
  // appliqué : tant qu'il en reste une, le backend ramène le dossier sur
  // « Pièces à déposer ». On les affiche ici pour que le choix soit fait en
  // connaissance de cause, plutôt que de laisser croire à un changement qui
  // sera écrasé dans la foulée.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await makeAuthenticatedApiCall('get', `/api/cg/${registrationId}/document-requests`);
        const rows = ((response.data as { data?: Array<{ doc_type: string; status: string }> })?.data ?? []).filter(
          (r) => r.status === 'open',
        );
        if (!cancelled) setOpenDocTypes(rows.map((r) => r.doc_type));
      } catch {
        if (!cancelled) setOpenDocTypes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, registrationId]);

  const keepsOpenRequests = selectedStatus === 'awaiting_docs' || selectedStatus === 'awaiting_docs_and_payment';
  const willCancelRequests = openDocTypes.length > 0 && !keepsOpenRequests;

  const handleOpenModal = () => setOpen(true);
  const handleCloseModal = () => setOpen(false);

  const handleConfirmChange = async () => {
    if (!selectedStatus) return;
    setLoading(true);
    try {
      const response = await makeAuthenticatedApiCall('put', `/api/cg/${registrationId}/cg-status`, {
        cgStatus: selectedStatus,
      });
      const data = response.data as { success: boolean; cgStatus?: string | null; applied?: boolean };
      if (data.success) {
        if (data.applied === false) {
          // Le backend a retenu autre chose que la cible (facture impayée,
          // réponse attendue…). Le dire, au lieu d'afficher un faux succès.
          window.alert(
            `Le dossier est passé en « ${CG_STATUS_LABEL_BY_VALUE[data.cgStatus ?? ''] ?? data.cgStatus} » et non « ${
              CG_STATUS_LABEL_BY_VALUE[selectedStatus] ?? selectedStatus
            } ».`,
          );
        }
        await onRefresh(undefined, true);
      } else {
        console.error('Failed to update CG status');
      }
    } catch (error) {
      console.error('Error updating CG status:', error);
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  // Groupe par catégorie pour rendu visuel clair.
  const groupedOptions = CG_STATUS_OPTIONS.reduce<Record<CgStatusOption['category'], CgStatusOption[]>>(
    (acc, opt) => {
      if (!acc[opt.category]) acc[opt.category] = [];
      acc[opt.category].push(opt);
      return acc;
    },
    {} as Record<CgStatusOption['category'], CgStatusOption[]>,
  );

  return (
    <>
      <Button variant="outlined" onClick={handleOpenModal}>
        🔀 Changer l'étape CG
      </Button>
      <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Changer le statut de la carte grise</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Sélectionne le statut canonique à appliquer. Le mapping vers les colonnes legacy (StageID Pipedrive +
            cg_review_status) est géré automatiquement par le backend dans une transaction unique.
          </Typography>

          {openDocTypes.length > 0 && (
            <Alert severity={willCancelRequests ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {openDocTypes.length} pièce(s) encore demandée(s) à l'agence :{' '}
              {openDocTypes.map(humanizeDocType).join(', ')}.
              {willCancelRequests
                ? " Valider ce statut annulera ces demandes — sans ça le dossier reviendrait automatiquement sur « Pièces à déposer »."
                : ' Le dossier restera en attente de ces pièces.'}
            </Alert>
          )}

          {(Object.keys(groupedOptions) as CgStatusOption['category'][]).map((cat, catIdx) => (
            <Box key={cat} sx={{ mb: 2.5 }}>
              {catIdx > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="overline" sx={{ fontWeight: 700 }}>
                  {CATEGORY_LABELS[cat]}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                {groupedOptions[cat].map((opt) => {
                  const isSelected = selectedStatus === opt.value;
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => setSelectedStatus(opt.value)}
                      sx={{
                        padding: theme.spacing(1.5),
                        cursor: 'pointer',
                        border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                        borderRadius: 1.5,
                        backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.paper,
                        transition: 'all 150ms ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.light,
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {opt.label}
                        </Typography>
                        <Chip label={opt.value} size="small" color={opt.color} sx={{ fontSize: 10, height: 18 }} />
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                        {opt.description}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button className="dealActionButton" onClick={handleCloseModal}>
            Annuler
          </Button>
          <Button
            className="secondaryDealActionButton"
            onClick={handleConfirmChange}
            color="primary"
            disabled={loading || !selectedStatus}
          >
            {loading ? <CircularProgress size={24} /> : 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CGStageModal;
